import { useState, useEffect } from 'react'
import type { JournalEntry, JournalEntryInsert } from '../lib/supabase'

const EMOTIONS = ['calm', 'confident', 'neutral', 'anxious', 'fearful', 'greedy'] as const
const OUTCOMES = ['win', 'loss', 'open'] as const

interface Props {
  entry: JournalEntry | null
  prefillTicker?: string | null
  open: boolean
  onClose: () => void
  onSave: (entry: Partial<JournalEntryInsert> & { id?: number }) => void
}

function RatingInput({ label, value, onChange }: { label: string; value: number | null; onChange: (v: number) => void }) {
  return (
    <div className="journal-field">
      <label>{label}</label>
      <div className="rating-dots">
        {Array.from({ length: 5 }, (_, i) => (
          <div
            key={i}
            className={`rating-dot ${value !== null && i < value ? 'filled' : ''} ${value !== null && i < value && value <= 2 ? 'bad' : ''} ${value !== null && i < value && value === 3 ? 'warn' : ''}`}
            onClick={() => onChange(i + 1)}
            style={{ cursor: 'pointer', width: 14, height: 14 }}
          />
        ))}
      </div>
    </div>
  )
}

export function TradeJournalEntry({ entry, prefillTicker, open, onClose, onSave }: Props) {
  const [ticker, setTicker] = useState('')
  const [tradeDate, setTradeDate] = useState(new Date().toISOString().split('T')[0])
  const [entryType, setEntryType] = useState('trade_review')
  const [thesis, setThesis] = useState('')
  const [postReview, setPostReview] = useState('')
  const [emotionBefore, setEmotionBefore] = useState<string | null>(null)
  const [emotionAfter, setEmotionAfter] = useState<string | null>(null)
  const [setupQuality, setSetupQuality] = useState<number | null>(null)
  const [ruleAdherence, setRuleAdherence] = useState<number | null>(null)
  const [executionQuality, setExecutionQuality] = useState<number | null>(null)
  const [outcome, setOutcome] = useState<string | null>(null)
  const [pnlAtReview, setPnlAtReview] = useState('')
  const [tagsInput, setTagsInput] = useState('')

  useEffect(() => {
    if (entry) {
      setTicker(entry.ticker || '')
      setTradeDate(entry.trade_date)
      setEntryType(entry.entry_type)
      setThesis(entry.thesis || '')
      setPostReview(entry.post_review || '')
      setEmotionBefore(entry.emotion_before)
      setEmotionAfter(entry.emotion_after)
      setSetupQuality(entry.setup_quality)
      setRuleAdherence(entry.rule_adherence)
      setExecutionQuality(entry.execution_quality)
      setOutcome(entry.outcome)
      setPnlAtReview(entry.pnl_at_review?.toString() || '')
      setTagsInput((entry.tags || []).join(', '))
    } else {
      setTicker(prefillTicker || '')
      setTradeDate(new Date().toISOString().split('T')[0])
      setEntryType('trade_review')
      setThesis('')
      setPostReview('')
      setEmotionBefore(null)
      setEmotionAfter(null)
      setSetupQuality(null)
      setRuleAdherence(null)
      setExecutionQuality(null)
      setOutcome(null)
      setPnlAtReview('')
      setTagsInput('')
    }
  }, [entry, prefillTicker, open])

  const handleSave = () => {
    const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean)
    onSave({
      ...(entry?.id ? { id: entry.id } : {}),
      account_id: 'live',
      trade_id: entry?.trade_id || null,
      ticker: ticker || null,
      entry_type: entryType,
      trade_date: tradeDate,
      thesis: thesis || null,
      post_review: postReview || null,
      emotion_before: emotionBefore,
      emotion_after: emotionAfter,
      setup_quality: setupQuality,
      rule_adherence: ruleAdherence,
      execution_quality: executionQuality,
      tags,
      outcome,
      pnl_at_review: pnlAtReview ? parseFloat(pnlAtReview) : null,
    })
    onClose()
  }

  return (
    <>
      <div className={`journal-panel-overlay ${open ? 'open' : ''}`} onClick={onClose} />
      <div className={`journal-panel ${open ? 'open' : ''}`}>
        <div className="journal-panel-header">
          <h3 style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: '0.95rem' }}>
            {entry ? 'Edit Entry' : 'New Journal Entry'}
          </h3>
          <button className="journal-btn" onClick={onClose}>ESC</button>
        </div>
        <div className="journal-panel-body">
          <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
            <div className="journal-field" style={{ flex: 1 }}>
              <label>Ticker</label>
              <input
                className="journal-input"
                value={ticker}
                onChange={e => setTicker(e.target.value.toUpperCase())}
                placeholder="SPY"
              />
            </div>
            <div className="journal-field" style={{ flex: 1 }}>
              <label>Date</label>
              <input
                className="journal-input"
                type="date"
                value={tradeDate}
                onChange={e => setTradeDate(e.target.value)}
              />
            </div>
          </div>

          <div className="journal-field">
            <label>Type</label>
            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              {['trade_review', 'daily_reflection', 'lesson', 'candidates'].map(t => (
                <button
                  key={t}
                  className={`journal-btn ${entryType === t ? 'primary' : ''}`}
                  onClick={() => setEntryType(t)}
                  style={{ fontSize: '0.7rem', padding: '4px 10px' }}
                >
                  {t.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="journal-field">
            <label>Thesis / Pre-trade notes</label>
            <textarea
              className="journal-textarea"
              value={thesis}
              onChange={e => setThesis(e.target.value)}
              placeholder="Why am I taking this trade? What's my edge?"
            />
          </div>

          <div className="journal-field">
            <label>Emotion Before</label>
            <div className="emotion-pills">
              {EMOTIONS.map(e => (
                <button
                  key={e}
                  className={`emotion-pill ${e} ${emotionBefore === e ? 'selected' : ''}`}
                  onClick={() => setEmotionBefore(emotionBefore === e ? null : e)}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div className="journal-field">
            <label>Emotion After</label>
            <div className="emotion-pills">
              {EMOTIONS.map(e => (
                <button
                  key={e}
                  className={`emotion-pill ${e} ${emotionAfter === e ? 'selected' : ''}`}
                  onClick={() => setEmotionAfter(emotionAfter === e ? null : e)}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-xl)' }}>
            <RatingInput label="Setup Quality" value={setupQuality} onChange={setSetupQuality} />
            <RatingInput label="Rule Adherence" value={ruleAdherence} onChange={setRuleAdherence} />
            <RatingInput label="Execution" value={executionQuality} onChange={setExecutionQuality} />
          </div>

          <div className="journal-field">
            <label>Outcome</label>
            <div className="journal-outcome-select">
              {OUTCOMES.map(o => (
                <button
                  key={o}
                  className={outcome === o ? o : ''}
                  onClick={() => setOutcome(outcome === o ? null : o)}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>

          <div className="journal-field">
            <label>P&L at Review</label>
            <input
              className="journal-input"
              type="number"
              value={pnlAtReview}
              onChange={e => setPnlAtReview(e.target.value)}
              placeholder="0.00"
              step="0.01"
            />
          </div>

          <div className="journal-field">
            <label>Post-trade Review</label>
            <textarea
              className="journal-textarea"
              value={postReview}
              onChange={e => setPostReview(e.target.value)}
              placeholder="What went well? What could I improve?"
            />
          </div>

          <div className="journal-field">
            <label>Tags (comma-separated)</label>
            <input
              className="journal-input"
              value={tagsInput}
              onChange={e => setTagsInput(e.target.value)}
              placeholder="momentum, breakout, semis"
            />
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-md)', justifyContent: 'flex-end', marginTop: 'var(--space-md)' }}>
            <button className="journal-btn" onClick={onClose}>Cancel</button>
            <button className="journal-btn primary" onClick={handleSave}>Save Entry</button>
          </div>
        </div>
      </div>
    </>
  )
}
